import {
  coreExtensionData,
  createExtensionInput,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { AsyncEntityProvider } from '@backstage/plugin-catalog-react';
import {
  defaultEntityContentGroupDefinitions,
  EntityContentBlueprint,
  EntityContextMenuItemBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { EntityLayoutBlueprint } from '@ipen-portal/plugin-common-react';
import { sortBy } from 'es-toolkit';
import { useMemo } from 'react';
import { z } from 'zod';

import { buildFilterFn } from '../filter/FilterWrapper';
import { useEntityFromUrl } from './useEntityFromUrl';

/**
 * @deprecated TODO can be Removed after the following MR is released:
 *               1. https://github.com/backstage/backstage/pull/31128
 */
export const CatalogEntityPage = PageBlueprint.makeWithOverrides({
  configSchema: {
    groups: z
      .array(
        z.record(
          z.string(),
          z.object({
            icon: z.string().optional(),
            title: z.string(),
          }),
        ),
      )
      .optional(),
    showNavItemIcons: z.boolean().optional().default(false),
  },
  factory(originalFactory, { config, inputs }) {
    const routes = inputs.contents.map(output => ({
      children: output.get(coreExtensionData.reactElement),
      filterFn: buildFilterFn(
        output.get(EntityContentBlueprint.dataRefs.filterFunction),
        output.get(EntityContentBlueprint.dataRefs.filterExpression),
      ),
      group: output.get(EntityContentBlueprint.dataRefs.group),
      icon: output.get(EntityContentBlueprint.dataRefs.icon),
      path: output.get(coreExtensionData.routePath),
      title: output.get(EntityContentBlueprint.dataRefs.title),
    }));

    return originalFactory({
      loader: async () => {
        const groupDefinitions =
          config.groups?.reduce((rest, group) => ({ ...rest, ...group }), {})
          ?? defaultEntityContentGroupDefinitions;

        const layouts = sortBy(
          inputs.layouts.map(header => ({
            component: header.get(EntityLayoutBlueprint.dataRefs.component),
            filter: header.get(EntityLayoutBlueprint.dataRefs.filterFunction),
            order: header.get(EntityLayoutBlueprint.dataRefs.order),
          })),
          [({ order }) => order, ({ filter }) => filter],
        );

        const Component = () => {
          const entityFromUrl = useEntityFromUrl();
          const { entity } = entityFromUrl;

          const Layout = layouts.find(
            l => !l.filter || (entity && l.filter(entity)),
          )?.component;

          const filteredRoutes = useMemo(
            () =>
              routes.flatMap(({ filterFn, ...route }) => {
                if (!entity || (filterFn && !filterFn(entity))) {
                  return [];
                }
                return [route];
              }),
            [entity],
          );

          return (
            <AsyncEntityProvider {...entityFromUrl}>
              {Layout && (
                <Layout
                  groupDefinitions={groupDefinitions}
                  routes={filteredRoutes}
                  showNavItemIcons={config.showNavItemIcons}
                />
              )}
            </AsyncEntityProvider>
          );
        };

        return <Component />;
      },
      noHeader: true,
      path: '/catalog/:namespace/:kind/:name',
      routeRef: catalogPlugin.routes.catalogEntity,
    });
  },
  inputs: {
    contents: createExtensionInput([
      coreExtensionData.reactElement,
      coreExtensionData.routePath,
      coreExtensionData.routeRef.optional(),
      EntityContentBlueprint.dataRefs.title,
      EntityContentBlueprint.dataRefs.filterFunction.optional(),
      EntityContentBlueprint.dataRefs.filterExpression.optional(),
      EntityContentBlueprint.dataRefs.group.optional(),
      EntityContentBlueprint.dataRefs.icon.optional(),
    ]),
    contextMenuItems: createExtensionInput([
      EntityContextMenuItemBlueprint.dataRefs.data,
      EntityContextMenuItemBlueprint.dataRefs.filterFunction.optional(),
    ]),
    layouts: createExtensionInput([
      EntityLayoutBlueprint.dataRefs.component,
      EntityLayoutBlueprint.dataRefs.order.optional(),
      EntityLayoutBlueprint.dataRefs.filterFunction.optional(),
    ]),
  },
  name: 'entity',
});
